import { useState } from "react";
import { useList } from "@refinedev/core";
import {
  CreditCard,
  TrendingUp,
  Calendar,
  DollarSign,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DataTable, type Column } from "@/components/tables";

interface Payment {
  id: number;
  amount: number;
  currency: string;
  status: string;
  provider: string;
  createdAt: string;
  description?: string;
}

interface UserFinancialsProps {
  userId: number;
  subscription?: {
    id: string;
    status: string;
    endDate?: string | null;
    planCode?: string | null;
  } | null;
}

/**
 * UserFinancials
 * 
 * Компонент UserFinancials для модуля users/components
 * 
 * @returns {React.ReactElement} React компонент
 */
export function UserFinancials({ userId, subscription }: UserFinancialsProps) {
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const { result, query } = useList<Payment>({
    resource: "payments",
    pagination: { currentPage: page, pageSize, mode: "server" },
    filters: [{ field: "userId", operator: "eq", value: userId }],
    sorters: [{ field: "createdAt", order: "desc" }],
  });

  const payments = result.data ?? [];
  const total = result.total ?? 0;
  const isLoading = query.isLoading;

  // Calculate LTV (sum of all payments)
  const ltv = payments.reduce((sum, p) => {
    if (p.status === "confirmed") {
      return sum + p.amount;
    }
    return sum;
  }, 0);

  const formatCurrency = (amount: number, currency = "RUB") => {
    return new Intl.NumberFormat("ru-RU", {
      style: "currency",
      currency,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("ru-RU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "confirmed":
        return (
          <Badge className="bg-green-500/10 text-green-500">Оплачен</Badge>
        );
      case "pending":
        return (
          <Badge className="bg-yellow-500/10 text-yellow-500">Ожидание</Badge>
        );
      case "rejected":
        return <Badge variant="destructive">Ошибка</Badge>;
      case "refunded":
        return <Badge variant="secondary">Возврат</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const columns: Column<Payment>[] = [
    {
      key: "createdAt",
      header: "Дата",
      render: (_, record) => formatDate(record.createdAt),
    },
    {
      key: "amount",
      header: "Сумма",
      className: "font-medium",
      render: (_, record) => formatCurrency(record.amount, record.currency),
    },
    {
      key: "status",
      header: "Статус",
      render: (_, record) => getStatusBadge(record.status),
    },
    {
      key: "provider",
      header: "Провайдер",
      hiddenOnMobile: true,
      render: (_, record) => {
        const providerLabels: Record<string, string> = {
          manual: "Вручную",
          click: "Click",
          payme: "Payme",
          stripe: "Stripe",
          multicard: "Multicard",
        };
        return providerLabels[record.provider] ?? record.provider;
      },
    },
    {
      key: "description",
      header: "Описание",
      className: "text-muted-foreground",
      hiddenOnMobile: true,
      render: (_, record) => record.description || "—",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">LTV</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(ltv)}</div>
            <p className="text-xs text-muted-foreground">
              Общая сумма платежей
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Платежей</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{total}</div>
            <p className="text-xs text-muted-foreground">Всего транзакций</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Следующее списание
            </CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {subscription?.endDate ? (
              <>
                <div className="text-2xl font-bold">
                  {formatDate(subscription.endDate)}
                </div>
                <p className="text-xs text-muted-foreground">
                  {subscription.planCode ?? "—"}
                </p>
              </>
            ) : (
              <div className="text-muted-foreground">Нет активной подписки</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Payments Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            История платежей
          </CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={payments}
            total={total}
            page={page}
            pageSize={pageSize}
            isLoading={isLoading}
            onPageChange={setPage}
            emptyMessage="Платежей пока нет"
          />
        </CardContent>
      </Card>
    </div>
  );
}
